import { AuthorizedDuotoneRounded } from "@hugeicons-pro/core-duotone-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { Trans } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCanGoBack, useNavigate, useRouter } from "@tanstack/react-router";
import { Button } from "~/components/ui/button";
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "~/components/ui/empty";
import { useAuthLogoutForm } from "~/forms/auth/logout";
import { cn } from "~/lib/utils";
import { useTRPC } from "~/trpc/client";

export function useRefreshSession() {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const router = useRouter();
	const navigate = useNavigate();

	const refreshMutation = useMutation(
		trpc.auth.refresh.mutationOptions({
			async onSuccess() {
				// Automatically refresh queries after a successful refresh
				await queryClient.invalidateQueries();

				await router.invalidate();
			},
			async onError() {
				// Clear all queries and redirect to login on error
				queryClient.clear();

				await navigate({ to: "/auth/login" });
			},
		}),
	);

	async function refreshSession() {
		await refreshMutation.mutateAsync();
	}

	return {
		refreshSession,
	};
}

export function SessionExpiredForm({
	className,
	hintEmail: _hintEmail,
	redirect: _redirect,
	...props
}: React.ComponentPropsWithoutRef<"div"> & {
	hintEmail?: string;
	redirect?: string;
}) {
	const router = useRouter();
	const canGoBack = useCanGoBack();
	const logoutForm = useAuthLogoutForm();
	const { refreshSession } = useRefreshSession();

	return (
		<div className={cn(className, "flex flex-1 items-center justify-center p-4")} {...props}>
			<Empty className="w-full">
				<EmptyHeader>
					<EmptyMedia variant="icon" className="bg-destructive/5 text-destructive">
						<HugeiconsIcon icon={AuthorizedDuotoneRounded} />
					</EmptyMedia>
					<EmptyTitle>Session expired</EmptyTitle>
					<EmptyDescription>
						Your session has expired. Please try to refresh or sign in again.
					</EmptyDescription>
				</EmptyHeader>
				<EmptyContent className="flex-row justify-center">
					<Button variant="outline" size="sm" onClick={() => refreshSession()}>
						<Trans>Reconnect</Trans>
					</Button>
					{canGoBack ? (
						<Button variant="outline" size="sm" onClick={() => router.history.back()}>
							Back
						</Button>
					) : null}
					<logoutForm.AppForm>
						<form
							className="contents"
							onSubmit={(e) => {
								e.preventDefault();
								e.stopPropagation();
								logoutForm.handleSubmit();
							}}
						>
							<logoutForm.FormButton type="submit" variant="link">
								<Trans>Sign out</Trans>
							</logoutForm.FormButton>
						</form>
					</logoutForm.AppForm>
				</EmptyContent>
			</Empty>
		</div>
	);
}

export function SessionExpiredToast({
	className,
	hintEmail: _hintEmail,
	redirect: _redirect,
	...props
}: React.ComponentPropsWithoutRef<"div"> & {
	hintEmail?: string;
	redirect?: string;
}) {
	const logoutForm = useAuthLogoutForm();
	const { refreshSession } = useRefreshSession();

	return (
		<div className={cn("flex flex-col gap-2", className)} {...props}>
			<p>
				<Trans>Your session expired, do you want to stay connected?</Trans>
			</p>
			<div className="flex items-center gap-4">
				<Button size="sm" onClick={() => refreshSession()}>
					<Trans>Stay connected</Trans>
				</Button>

				<logoutForm.AppForm>
					<form
						className="contents"
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							logoutForm.handleSubmit();
						}}
					>
						<logoutForm.FormButton size="sm" type="submit" variant="link">
							<Trans>Sign out</Trans>
						</logoutForm.FormButton>
					</form>
				</logoutForm.AppForm>
			</div>
		</div>
	);
}
